/**
 * FERP PDF Thumbnail Panel (DRR-079)
 *
 * Page thumbnails with IntersectionObserver for lazy rendering.
 * Clicking a thumbnail navigates to that page.
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import { Stack, Box, Text, UnstyledButton } from '@mantine/core';
import { usePdfSuite } from './PdfSuiteContext';

export function ThumbnailPanel() {
  const { state, setCurrentPage } = usePdfSuite();
  const { numPages, currentPage } = state.viewer;

  if (numPages === 0) {
    return (
      <Box p="sm">
        <Text size="xs" c="dimmed">No pages loaded</Text>
      </Box>
    );
  }

  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <Stack gap={4} p="xs">
      {pages.map((pageNum) => (
        <ThumbnailItem
          key={pageNum}
          pageNum={pageNum}
          isActive={pageNum === currentPage}
          onClick={() => setCurrentPage(pageNum)}
        />
      ))}
    </Stack>
  );
}

function ThumbnailItem({
  pageNum,
  isActive,
  onClick,
}: {
  pageNum: number;
  isActive: boolean;
  onClick: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-scroll active thumbnail into view
  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isActive]);

  return (
    <UnstyledButton
      ref={ref}
      onClick={onClick}
      style={{
        border: isActive
          ? '2px solid var(--mantine-color-blue-5)'
          : '1px solid var(--mantine-color-gray-3)',
        borderRadius: 4,
        padding: 4,
        backgroundColor: isActive ? 'var(--mantine-color-blue-0)' : 'white',
        textAlign: 'center',
        minHeight: 80,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isVisible ? (
        <>
          <Box
            style={{
              width: '100%',
              height: 60,
              backgroundColor: 'var(--mantine-color-gray-1)',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text size="lg" c="dimmed" fw={300}>
              {pageNum}
            </Text>
          </Box>
          <Text size="xs" mt={2} c={isActive ? 'blue' : 'dimmed'}>
            Page {pageNum}
          </Text>
        </>
      ) : (
        <Text size="xs" c="dimmed">Page {pageNum}</Text>
      )}
    </UnstyledButton>
  );
}
